import { CollectionType, SearchResultType } from '../../lib/types'
import TextField from '@mui/material/TextField'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import debounce from 'lodash/debounce'
import api from '../../lib/api'
import { InputBaseProps } from '@mui/material/InputBase'
import { CreateCollectionContext } from '../CreateCollection'

type CollectionTypeOption = Partial<SearchResultType> & {
  label: string
}

const filter = createFilterOptions<CollectionTypeOption>()

interface iCollectionSearch {
  handleSelect: (collection: CollectionType | null) => void
  allowAdd: boolean
  onBlur?: InputBaseProps['onBlur']
  error?: boolean
  helperText: string
  name?: string
  filterId?: string
}

const CollectionSearch: React.FC<iCollectionSearch> = ({
  handleSelect,
  allowAdd = false,
  onBlur,
  name,
  helperText = ' ',
  filterId,
  error
}) => {
  const { actions: createCollectionActions } = useContext(CreateCollectionContext)
  const [optionsState, setOptions] = useState<readonly CollectionTypeOption[]>([])
  const [value, setValue] = useState<CollectionTypeOption | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [newOption, setNewOption] = useState<CollectionTypeOption | null>(null)
  const [inputValue, setInputValue] = useState<string>('')

  const fetch = useMemo(
    () =>
      debounce(async (query: string, callback: (results: readonly CollectionTypeOption[]) => void) => {
        setLoading(true)
        const { content } = await api.searchCollections(0, 100, query)
        callback(content.filter(i => i.kind === 'COLLECTION').map(p => ({ ...p, label: p.title })))
        setLoading(false)
      }, 1500),
    []
  )

  const fetchCallback = (results?: readonly CollectionTypeOption[]) => {
    let newOptions: readonly CollectionTypeOption[] = []

    if (value) {
      newOptions = [value]
    }

    if (results) {
      newOptions = [
        ...newOptions,
        ...results.filter(function (item) {
          return item?.id !== value?.id
        })
      ]
    }

    // @ts-ignore
    setOptions(newOptions)
  }

  useEffect(() => {
    fetch(inputValue, fetchCallback)
  }, [])

  useEffect(() => {
    if (value) {
      // @ts-ignore
      setOptions([value])
    }

    ;(async function () {
      if (value?.id) {
        const collection = await api.getCollection(value.id)
        handleSelect(collection)
      }
    })()
  }, [value])

  useEffect(() => {
    let active = true

    setLoading(true)

    if (active) {
      fetch(inputValue, fetchCallback)
    }

    return () => {
      active = false
    }
  }, [inputValue, fetch])

  const addCallback = (collection: CollectionType | null) => {
    if (!collection) {
      setValue(null)
      return
    }

    const option: CollectionTypeOption = {
      id: collection.id,
      title: collection.title,
      label: collection.title,
      kind: 'COLLECTION'
    }

    setOptions([option])
    setNewOption(option)
  }

  useEffect(() => {
    setValue(newOption)
  }, [newOption])

  return (
    <Autocomplete
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      onChange={(event, newValue) => {
        event.preventDefault()
        // @ts-ignore
        if (newValue?.id === 'new place') {
          createCollectionActions.open(addCallback, inputValue)
          setInputValue('')
          return
        }
        setValue(newValue)
      }}
      onBlur={e => {
        e.preventDefault()
        // @ts-ignore
        return onBlur && onBlur(e)
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params)
        // Suggest the creation of a new value
        const isExisting = options.some(option => inputValue === option.title)
        if (inputValue !== '' && !isExisting && allowAdd) {
          filtered.push({
            id: 'new place',
            title: inputValue,
            label: `Click here to add "${inputValue}" as a new collection in Rakonto.`
          })
        }

        return filtered
      }}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.label}
          </li>
        )
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Type and select a collection title"
          name={name}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            type: 'search',
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )}
              </React.Fragment>
            )
          }}
        />
      )}
      options={optionsState.filter(item => item.id !== filterId)}
      fullWidth
      getOptionLabel={option => option!.title || ''}
      inputValue={inputValue}
      value={value || undefined}
      size="small"
      loading={loading}
    />
  )
}

export default CollectionSearch
